/**
 * Gradient Button Animation Controller
 * Handles hover animations for SVG gradient buttons
 */
export default class GradientButton {
    constructor() {
        this.buttons = document.querySelectorAll('.gradient-button');
        this.init();
        this.setupResizeHandler();
    }

    init() {
        this.buttons.forEach(button => {
            this.adjustButtonSize(button);
            this.setupButtonEvents(button);
        });
    }

    setupResizeHandler() {
        // Handle resize for regular buttons (exclude full-width buttons)
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                this.buttons.forEach(button => {
                    const svg = button.querySelector('.gradient-button-svg');
                    // Only process regular buttons, not full-width ones
                    if (svg && !svg.hasAttribute('data-full-width')) {
                        this.adjustButtonSize(button);
                    }
                });
            }, 100); // Debounce resize events
        });
    }

    adjustButtonSize(button) {
        const svg = button.querySelector('.gradient-button-svg');
        const text = svg.querySelector('text');
        const rect = svg.querySelector('rect');

        // Skip size adjustment for buttons without text (icon buttons)
        if (!text) {
            return;
        }

        // Skip full-width buttons - they will be handled by mobile menu opening
        if (svg.hasAttribute('data-full-width')) {
            return;
        }

        // Create a temporary canvas to measure text width
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = '500 18px sans-serif'; // Match the SVG text style

        const textWidth = context.measureText(text.textContent).width;
        const horizontalPadding = 44; // 22px on each side
        let newWidth = textWidth + horizontalPadding;

        // If SSR estimated a width close to this, keep it to avoid tiny shifts
        const currentWidth = parseFloat(svg.getAttribute('width')) || 0;
        if (currentWidth && Math.abs(currentWidth - newWidth) <= 4) {
            newWidth = currentWidth;
        }

        // Height with 14px vertical padding on each side
        const svgHeight = 52; // 14px + text height + 14px

        // Update SVG dimensions
        svg.setAttribute('width', newWidth);
        svg.setAttribute('height', svgHeight);
        rect.setAttribute('width', newWidth - 3);
        rect.setAttribute('height', svgHeight - 3);
        text.setAttribute('x', newWidth / 2);
        text.setAttribute('y', svgHeight / 2 + 6); // Center vertically with baseline offset
    }


    setupButtonEvents(button) {
        const svg = button.querySelector('.gradient-button-svg');
        const animations = svg.querySelectorAll('animate');
        let resetTimeout = null;

        // Check if this is a submenu gradient button
        if (button.classList.contains('submenu-gradient-button')) {
            // Find the parent submenu item
            const submenuItem = button.closest('.submenu-item');
            if (submenuItem) {
                submenuItem.addEventListener('mouseenter', () => {
                    if (resetTimeout) {
                        clearTimeout(resetTimeout);
                        resetTimeout = null;
                    }
                    this.cancelSmoothReset(svg);
                    this.startAnimation(animations);
                });

                submenuItem.addEventListener('mouseleave', () => {
                    // Freeze at current frame by stopping the loop
                    this.stopAnimation(animations);
                });
            }
        } else {
            // Regular button hover behavior
            button.addEventListener('mouseenter', () => {
                if (resetTimeout) {
                    clearTimeout(resetTimeout);
                    resetTimeout = null;
                }
                this.cancelSmoothReset(svg);
                this.startAnimation(animations);
            });

            button.addEventListener('mouseleave', () => {
                // Freeze at current frame by stopping the loop
                this.stopAnimation(animations);
            });
        }
    }

    startAnimation(animations) {
        animations.forEach(animation => {
            animation.beginElement();
        });
    }

    stopAnimation(animations) {
        animations.forEach(animation => {
            animation.endElement();
        });
    }

    // Smooth reset removed per updated behavior

    cancelSmoothReset(svg) {
        if (svg.__gradientResetRAF) {
            cancelAnimationFrame(svg.__gradientResetRAF);
            svg.__gradientResetRAF = null;
        }
    }

    // Helper functions no longer needed; keeping stubs if future changes require smoothing
}